import {Injectable} from '@angular/core';
import {Subscription} from 'rxjs';
import {MapLayersManager} from '../map-layers-manager';
import {GeoJSONSourceSpecification} from 'maplibre-gl';
import {SettingsService, ActivityFilter} from '../../../../configuration/settings.service';
import {ServerEventService} from '../../../../data/server-events/server-event.service';
import { LocationApiService } from '../../../../data/location-api/location-api.service';
import { ConfigurationService } from '../../../../configuration/configuration.service';
import { ConfigurationModel } from '../../../models/configuration.model';

@Injectable({
  providedIn: 'root'
})
export class RoadStatusService {

  static readonly ROAD_SEGMENTS_SOURCE_ID = 'road-segments';
  private mapLayersManager: MapLayersManager;
  private isEnabled: boolean;
  layerFilter: ActivityFilter = ActivityFilter.NONE;
  private contentVisibilityCutoff: Date = null;

  private readonly openSubscriptions = Array<Subscription>();

  constructor(private settingsService: SettingsService,
              private readonly configurationService: ConfigurationService,
              private serverEventService: ServerEventService,
              private locationApi: LocationApiService,
  ) { }

  init(mapLayersManager: MapLayersManager, configurationModel: ConfigurationModel, isEnabled: boolean) {
    if (!!this.mapLayersManager) {
      throw Error('The map layers manager has already been set.');
    }
    this.mapLayersManager = mapLayersManager;
    this.contentVisibilityCutoff = configurationModel.contentVisibilityCutoff;
    this.isEnabled = isEnabled;
    this.addSource();

    if (this.isEnabled) {
      this.connectToManager();
    }
  }

  release() {
    if (!this.mapLayersManager) {
      throw Error('The map has not been set!');
    }

    for (const subscription of this.openSubscriptions) {
      subscription.unsubscribe();
    }
    this.openSubscriptions.length = 0;
    this.mapLayersManager = null;
  }

  connectToManager() {
    const that = this;

    // handle refresh on derivedLayerCache update
    const cacheChangeSubscription = this.serverEventService.derivedLayerCacheUpdateObservable.subscribe({
      next(/*cacheUpdate*/) {
        that.handleSourceRefresh();
      }
    });
    this.openSubscriptions.push(cacheChangeSubscription);

    const changesSubscription = this.settingsService.settingsChangedObservable.subscribe({
      next(newSettings) {
        if (newSettings.key === SettingsService.ACTIVITY_FILTER) {
          that.layerFilter = newSettings.value;
          that.handleSourceRefresh();
        }
      }
    });
    this.openSubscriptions.push(changesSubscription);

    this.openSubscriptions.push(
      this.configurationService.sharedConfigurationModel.subscribe(model => {
        if (model && this.contentVisibilityCutoff !== model.contentVisibilityCutoff) {
          this.contentVisibilityCutoff = model.contentVisibilityCutoff;
          this.updateSource();
        }
      })
    );
  }

  getRoadSegmentFeatures() {
    return this.locationApi.getRoadSegments(
      ActivityFilter.toFlagMask(this.layerFilter),
      this.contentVisibilityCutoff,
    );
  }

  addSource() {
    this.mapLayersManager.addSource(RoadStatusService.ROAD_SEGMENTS_SOURCE_ID, this.getEmptySource());
    if (this.isEnabled) {
      this.updateSource();
    }
  }

  private getEmptySource(): GeoJSONSourceSpecification {
    return {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: []
      }
    } as GeoJSONSourceSpecification;
  }

  private updateSource() {
    this.getRoadSegmentFeatures().then(response => {
      this.mapLayersManager
        ?.getGeoJsonSource(RoadStatusService.ROAD_SEGMENTS_SOURCE_ID)
        ?.setData(response);
    });
  }

  private handleSourceRefresh() {
    this.updateSource();
  }
}
